import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { NotificationService } from '../services/notification.service';
import { ErrorHandlingService } from '../services/error-handling.service';
import { AuthService } from '../../features/auth/services/auth.service';
import { ERROR_MESSAGES } from '../constants/validation-messages';

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const notificationService = inject(NotificationService);
  const errorHandlingService = inject(ErrorHandlingService);
  const router = inject(Router);
  const authService = inject(AuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const skipToast =
        req.url.includes('/LoginManagement') ||
        req.url.includes('/LoginApplication') ||
        req.url.includes('/api/config');

      if (!skipToast) {
        const errorMessage = errorHandlingService.extractErrorMessage(error);
        const status = error.status;

        const isSellerProfileRequest = req.url.includes('/SellerProfile');

        if (status === 404 && isSellerProfileRequest) {
          return throwError(() => error);
        }

        if (status === 401) {
          // Check if it's a login endpoint to avoid redirect loop
          const isLoginEndpoint =
            req.url.includes('/LoginManagement') ||
            req.url.includes('/LoginApplication') ||
            req.url.includes('/auth/login');

          // If not a login endpoint, it's likely token expired - redirect to login
          if (!isLoginEndpoint) {
            // Clear authentication info
            authService.logout();

            // Get current URL for return after login
            const currentUrl = router.url;

            // Redirect to login page
            router.navigate(['/auth/login'], {
              queryParams: { returnUrl: currentUrl },
            });

            // Show notification
            notificationService.showError(
              'Your session has expired. Please log in again.',
              'Session Expired'
            );
          } else {
            // For login endpoints, just show the error
            notificationService.showError(ERROR_MESSAGES.unauthorized, 'Authentication Error');
          }
        } else if (status === 403) {
          notificationService.showError(ERROR_MESSAGES.forbidden, 'Access Denied');
        } else if (status === 404) {
          notificationService.showError(ERROR_MESSAGES.resourceNotFound, 'Not Found');
        } else if (status >= 500) {
          notificationService.showError(ERROR_MESSAGES.serverError, 'Server Error');
        } else {
          notificationService.showError(errorMessage, 'Error');
        }
      }

      return throwError(() => error);
    })
  );
};
